import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParamsControls } from "@/search/Langston/useSearchParamsControls";
import useStandardAggregationsQuery from "@/home/TeacherHomepage/services/useStandardAggregationsQuery";
import { useStandardsFetch } from "./hooks";
import {
  IStandardsSearchContext,
  standardsSearchProviderInitialValue,
  TOTAL_RESULTS_LIMIT_PER_PAGE,
} from "./StandardsSearchProvider.constants";

const StandardsSearchContext = createContext<IStandardsSearchContext>(
  standardsSearchProviderInitialValue,
);

export const useStandardsSearchContext = () =>
  useContext(StandardsSearchContext);

interface StandardsSearchProviderProps {
  children: ReactElement;
}

const StandardsSearchProvider = ({
  children,
}: StandardsSearchProviderProps) => {
  // For the standards search, we need to use the search params from the URL, this will be use as a fallback on useStandardsQuery
  const [searchParams, setSearchParams] = useState(new URLSearchParams());
  const { getSearchParamValues } = useSearchParamsControls({
    searchParams,
    setSearchParams,
  });

  const [standardsSearchOptions, setStandardsSearchOptions] = useState<{
    currentPage: number;
    currentNeedle: string;
    shouldFetchStandards: boolean;
    subjects?: string;
    adoption_years?: string;
  }>({
    currentPage: 1,
    currentNeedle: "",
    shouldFetchStandards: false,
  });

  const setCurrentNeedle = useCallback(
    (currentNeedle: string) =>
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentNeedle,
        shouldFetchStandards: true,
      }),
    [standardsSearchOptions],
  );

  const {
    allStandardsModalResults,
    selectedStandards,
    updateSelectedStandardsOnChange,
    top5Standards,
    totalStandardsResults,
    isLoadingStandardsResults,
    fetchFilteredStandards,
    standardsInQuery,
    searchParamsObj,
    resetStandardsList,
    setSelectedStandards,
    getTemporarySelectedFilters,
    clearTemporarySelectedFilters,
  } = useStandardsFetch(
    standardsSearchOptions.currentPage,
    standardsSearchOptions.currentNeedle,
    {
      searchParams,
      getSearchParamValues,
    },
  );

  const { data: standardAggregationsData } = useStandardAggregationsQuery();

  const standardAggregations = standardAggregationsData?.results || null;

  const handleViewMoreStandards = useCallback(() => {
    const { currentPage, currentNeedle } = standardsSearchOptions;
    setStandardsSearchOptions({
      ...standardsSearchOptions,
      currentPage: currentPage + 1,
    });
    fetchFilteredStandards({
      page: currentPage,
      standard_needle: currentNeedle,
    });
  }, [searchParamsObj, standardsSearchOptions]);

  const handleStandardsSearch = useCallback(
    (needle?: string, subject?: string, year?: string) => {
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentNeedle: needle || "",
        currentPage: 1,
      });
      fetchFilteredStandards({
        standard_needle: needle,
        page: 0,
        ...(subject !== "-" ? { subjects: subject } : {}),
        ...(year !== "-" ? { adoption_years: year } : {}),
      });
    },
    [searchParamsObj, standardsSearchOptions],
  );

  const resetStandardsPagination = useCallback(() => {
    const { shouldFetchStandards, currentNeedle } = standardsSearchOptions;
    if (shouldFetchStandards) {
      fetchFilteredStandards({
        standard_needle: currentNeedle,
        page: 0,
      });
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentPage: 1,
      });
    }
  }, [searchParamsObj, standardsSearchOptions]);

  const resetStandardsSearch = () => {
    resetStandardsList();
    setStandardsSearchOptions({
      currentNeedle: "",
      currentPage: 1,
      shouldFetchStandards: false,
    });
  };

  const showViewMoreButton = useMemo(
    () =>
      allStandardsModalResults &&
      !isLoadingStandardsResults &&
      standardsSearchOptions.currentPage <
        Math.ceil(totalStandardsResults / TOTAL_RESULTS_LIMIT_PER_PAGE),
    [
      allStandardsModalResults,
      standardsSearchOptions.currentPage,
      isLoadingStandardsResults,
      totalStandardsResults,
    ],
  );

  useEffect(() => {
    resetStandardsPagination();
  }, [standardsSearchOptions.currentNeedle]);

  return (
    <StandardsSearchContext.Provider
      value={{
        top5Standards,
        selectedStandards,
        updateSelectedStandardsOnChange,
        allStandardsModalResults,
        standardsInQuery,
        totalStandardsResults,
        fetchFilteredStandards,
        isLoadingStandardsResults,
        handleViewMoreStandards,
        currentNeedle: standardsSearchOptions.currentNeedle,
        setCurrentNeedle,
        currentPage: standardsSearchOptions.currentPage,
        handleStandardsSearch,
        resetStandardsSearch,
        showViewMoreButton,
        setSelectedStandards,
        getTemporarySelectedFilters,
        clearTemporarySelectedFilters,
        standardAggregations,
      }}
    >
      {children}
    </StandardsSearchContext.Provider>
  );
};

export { StandardsSearchProvider };
